import Button from 'react-bootstrap/Button';
import CloseButton from 'react-bootstrap/CloseButton';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import React, { useState, useEffect, useMemo } from "react";
import { generateTileSVG } from '../utils/tileGenerator';
import { getGridPixelSize, TILE_BORDER_RADIUS } from '../utils/tileLayout';
import { COLORS } from '../utils/colors';
import EdgeConnectorOverlay from './EdgeConnectorOverlay';
import confetti from 'canvas-confetti';

interface HelpModalProps {
  onHelpClose: ()=>void,
  showModal: boolean,
  howToImg: any,
  mode: string,
  showMatchingEdges?: boolean,
  onToggleMatchingEdges?: (value: boolean) => void,
}

// Define the 5-move sequence (swapping positions) - constant outside component
const MOVE_SEQUENCE = [
  [0, 1],
  [1, 4],
  [2, 8],
  [4, 3],
  [4, 7],
  [5, 6]
];

const SWAP_ANIMATION_MS = 400;
const INITIAL_DELAY_MS = 600;
const MOVE_INTERVAL_MS = 950;
const HELP_TILE_SIZE = 75;
const HELP_GRID_GAP = 3;
const HELP_CONNECTOR_THICKNESS = 3;

function HelpModal(props: HelpModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  
  // Define the tile patterns for the demonstration
  // Different tiles for classic vs casual mode
  const classicInitialTiles = [
    "WWWBBBBW",
    "WBBBWWWW",
    "BBWWWBWW",
    "WBWBWWBB",
    "BBBWBWWW",
    "WBBWBWBW",
    "WWWWBWBB",
    "BWWWBWBB",
    "WBBBBWWB"
  ];
  
  const casualInitialTiles = [
    "BWWWLBBW",
    "WBBLWWWL",
    "LBWBBBWL",
    "BWWLLBWL",
    "LLBLBLLW",
    "WBBBLLWW",
    "LLWWBWBB",
    "WLLLBWWL",
    "LLLLWWLB"
  ];
  
  const initialTiles = useMemo(() => {
    return props.mode === 'casual' ? casualInitialTiles : classicInitialTiles;
  }, [props.mode]);
  
  const [tiles, setTiles] = useState(initialTiles);
  const [positions, setPositions] = useState([0, 1, 2, 3, 4, 5, 6, 7, 8]);
  const [connectorPositions, setConnectorPositions] = useState([0, 1, 2, 3, 4, 5, 6, 7, 8]);

  // Keep edge glows in sync with tile positions only after each swap animation finishes
  useEffect(() => {
    if (!props.showModal) return;

    const timeout = setTimeout(() => {
      setConnectorPositions(positions);
    }, SWAP_ANIMATION_MS);

    return () => clearTimeout(timeout);
  }, [positions, props.showModal]);

  // Start the animation immediately when the modal opens
  useEffect(() => {
    if (!props.showModal) {
      setCurrentStep(0);
      setTiles(initialTiles);
      setPositions([0, 1, 2, 3, 4, 5, 6, 7, 8]);
      setConnectorPositions([0, 1, 2, 3, 4, 5, 6, 7, 8]);
      return;
    }

    let initialTimeout: NodeJS.Timeout | null = null;
    let interval: NodeJS.Timeout | null = null;

    if (currentStep === 0 && MOVE_SEQUENCE.length > 0) {
      initialTimeout = setTimeout(() => {
        const [pos1, pos2] = MOVE_SEQUENCE[0];
        setPositions(prev => {
          const tile1Index = prev.indexOf(pos1);
          const tile2Index = prev.indexOf(pos2);
          const newPositions = [...prev];
          newPositions[tile1Index] = pos2;
          newPositions[tile2Index] = pos1;
          return newPositions;
        });
        setCurrentStep(1);
      }, INITIAL_DELAY_MS);
      return () => initialTimeout && clearTimeout(initialTimeout);
    }

    interval = setInterval(() => {
      if (currentStep < MOVE_SEQUENCE.length) {
        const [pos1, pos2] = MOVE_SEQUENCE[currentStep];
        setPositions(prev => {
          const tile1Index = prev.indexOf(pos1);
          const tile2Index = prev.indexOf(pos2);
          const newPositions = [...prev];
          newPositions[tile1Index] = pos2;
          newPositions[tile2Index] = pos1;
          return newPositions;
        });
        // Shoot confetti when the final move's animation completes
        if (currentStep === MOVE_SEQUENCE.length - 1) {
          setTimeout(() => {
            confetti({
              particleCount: 300,
              spread: 360,
              origin: { x: 0.5, y: 0.5 },
              zIndex: 9999,
              decay: 0.85,
              ticks: 75
            });
          }, SWAP_ANIMATION_MS);
        }
        setCurrentStep(prev => prev + 1);
      } else if (currentStep === MOVE_SEQUENCE.length) {
        setCurrentStep(prev => prev + 1);
      } else {
        setCurrentStep(0);
        setTiles(initialTiles);
        setPositions([0, 1, 2, 3, 4, 5, 6, 7, 8]);
      }
    }, MOVE_INTERVAL_MS);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentStep, props.showModal, initialTiles]);

  const tileSize = HELP_TILE_SIZE;
  const columns = 3;
  const { width: gridWidth, height: gridHeight } = getGridPixelSize(
    tiles.length,
    columns,
    tileSize,
    HELP_GRID_GAP
  );

  const gridOrderedTiles = useMemo(() => {
    const grid: string[] = [];
    tiles.forEach((pattern, tileIndex) => {
      grid[connectorPositions[tileIndex]] = pattern;
    });
    return grid;
  }, [tiles, connectorPositions]);

  return (
    <>
      <Modal
        show={props.showModal}
        className='help-modal'
        size='lg'
        animation={true}
      >
        <div className="close-button-container">
          <CloseButton onClick={() => props.onHelpClose()} />
        </div>
        <Modal.Body style={{ marginTop: 5 }}>
          <div style={{fontSize: 48, fontWeight: 'bold'}}> How To Play </div>
          <div style={{fontSize: 24}}> Swap tiles until the colors match<br/>on every shared side </div>
          
          {/* Interactive tile demonstration */}
          <div
            style={{
              margin: '20px auto',
              padding: HELP_GRID_GAP,
              backgroundColor: COLORS.GRID_BACKDROP,
              borderRadius: TILE_BORDER_RADIUS + HELP_GRID_GAP,
              width: 'fit-content',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: gridWidth,
                height: gridHeight,
              }}
            >
              <EdgeConnectorOverlay
                tiles={gridOrderedTiles}
                columns={columns}
                tileSize={tileSize}
                show={props.showMatchingEdges ?? true}
                zIndex={0}
                gap={HELP_GRID_GAP}
                connectorThickness={HELP_CONNECTOR_THICKNESS}
              />
              {tiles.map((pattern, index) => {
                const currentPos = positions[index];
                const row = Math.floor(currentPos / columns);
                const col = currentPos % columns;
                const cellStride = tileSize + HELP_GRID_GAP;

                return (
                  <img
                    key={`${index}-${pattern}`}
                    src={`data:image/svg+xml,${encodeURIComponent(generateTileSVG(pattern, tileSize))}`}
                    alt="tile"
                    width={tileSize}
                    height={tileSize}
                    style={{
                      borderRadius: TILE_BORDER_RADIUS,
                      boxShadow:
                        'rgb(63 63 68 / 5%) 0px 0px 0px 1px, rgb(34 33 81 / 15%) 0px 1px 3px 0px',
                      cursor: 'default',
                      transition: `all ${SWAP_ANIMATION_MS}ms ease-in-out`,
                      position: 'absolute',
                      top: `${row * cellStride}px`,
                      left: `${col * cellStride}px`,
                      zIndex: currentStep > MOVE_SEQUENCE.length ? 10 : 1,
                    }}
                  />
                );
              })}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 16,
            }}
          >
            <Form.Check
              type="switch"
              id="show-matching-edges-toggle"
              label="Show matching edges"
              checked={props.showMatchingEdges ?? true}
              onChange={(e) => props.onToggleMatchingEdges?.(e.target.checked)}
              style={{ fontSize: 20, marginBottom: 0 }}
            />
          </div>
          
          <Button 
            className="game-button"
            onClick={() => props.onHelpClose()} 
            style={{width: 120, marginTop: 20}}>
            Play
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default HelpModal;